// Schedule Inspection Modal
// Allows users to set or modify the next inspection date for a compliance item

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format, differenceInDays } from 'date-fns';
import { Calendar as CalendarIcon, Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Form validation schema
const formSchema = z.object({
  newInspectionDate: z.date({
    message: 'New inspection date is required',
  }).refine((date) => date > new Date(), {
    message: 'New inspection date must be in the future',
  }),
  assignedInspector: z.string().optional(),
  schedulingNotes: z.string().optional(),
  sendNotification: z.boolean().default(true),
});

type FormValues = z.infer<typeof formSchema>;

interface ScheduleInspectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  complianceItem?: {
    id: number;
    assetName: string;
    regimeName: string;
    currentDueDate?: string;
    frequency?: string;
  };
  onSubmit: (data: FormValues) => Promise<void>;
}

// Mock inspectors - in real app, this would come from API
const MOCK_INSPECTORS = [
  { value: '1', label: 'John Smith' },
  { value: '2', label: 'Sarah Johnson' },
  { value: '3', label: 'Mike Williams' },
  { value: '4', label: 'Emily Brown' },
];

export function ScheduleInspectionModal({
  open,
  onOpenChange,
  complianceItem,
  onSubmit,
}: Readonly<ScheduleInspectionModalProps>) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newInspectionDate: undefined as unknown as Date,
      assignedInspector: '',
      schedulingNotes: '',
      sendNotification: true,
    },
  });

  // Check if date is too far in the future
  const checkDateWarning = (date: Date) => {
    const daysUntil = differenceInDays(date, new Date());
    
    // Warn if more than 2 years in the future
    if (daysUntil > 730) {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  };

  const handleSubmit = async (data: FormValues) => {
    try {
      setIsSubmitting(true);
      await onSubmit(data);
      toast.success('Inspection scheduled successfully');
      form.reset();
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to schedule inspection');
      console.error('Error scheduling inspection:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Schedule Inspection</DialogTitle>
          <DialogDescription>
            {complianceItem ? (
              <>
                Set or modify the next inspection date for <strong>{complianceItem.assetName}</strong> - {complianceItem.regimeName}
              </>
            ) : (
              'Set or modify the next inspection date for a compliance item'
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Current Inspection Date (Read-only) */}
            {complianceItem?.currentDueDate && (
              <div className="rounded-lg border p-4 bg-muted/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Current Inspection Date</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {format(new Date(complianceItem.currentDueDate), 'PPP')}
                    </p>
                  </div>
                  {complianceItem.frequency && (
                    <div className="text-right">
                      <p className="text-sm font-medium">Frequency</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {complianceItem.frequency}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* New Inspection Date */}
            <FormField
              control={form.control}
              name="newInspectionDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>New Inspection Date *</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground'
                          )}
                        >
                          {field.value ? (
                            format(field.value, 'PPP')
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={(date) => {
                          field.onChange(date);
                          if (date) checkDateWarning(date);
                        }}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    The date must be in the future
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Warning for dates too far in future */}
            {showWarning && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  The selected date is more than 2 years in the future. Please verify this is correct.
                </AlertDescription>
              </Alert>
            )}

            {/* Assigned Inspector */}
            <FormField
              control={form.control}
              name="assignedInspector"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Assigned Inspector (Optional)</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an inspector" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MOCK_INSPECTORS.map((inspector) => (
                        <SelectItem key={inspector.value} value={inspector.value}>
                          {inspector.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Optionally assign an inspector to this scheduled inspection
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Scheduling Notes */}
            <FormField
              control={form.control}
              name="schedulingNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Scheduling Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add any notes about this scheduled inspection"
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Send Notification */}
            <FormField
              control={form.control}
              name="sendNotification"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Send notification to assigned inspector
                    </FormLabel>
                    <FormDescription>
                      {field.value
                        ? 'A calendar invitation will be sent to the assigned inspector'
                        : 'No notification will be sent'}
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Schedule Inspection
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
